import styled from 'styled-components';
import { Plus, StackSimple, Tray } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { Flex, Select } from 'antd';
import { RackDetailHook } from '../../../../../hooks';
import { MuteText, ShadowBox } from '../../../../../elements';
import { CommonUtility, theme } from '../../../../../utility';
import {
  AddBayPopup,
  EditBayPopup,
  RackDimensionsDetails,
} from '../../../../../page-components';
import { WarehouseSettingsBreadcrumb } from '../../../../../page-components/zones';
import { useWarehouseDetails } from '../../warehouse';
import { ZoneDetailHook } from '../../../../../hooks/zone';

const LocationRenderer = styled.div`
  padding: 10px 25px;
  display: flex;
  column-gap: 10px;
  row-gap: 10px;
  flex-wrap: wrap;
  border-bottom: 15px solid #dbdbdf;
  .name {
    position: absolute;
    bottom: -3px;
    left: 50%;
    font-size: 12px;
    transform: translate(-50%);
    z-index: 10;
  }
`;

const LocationContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 94px;
  width: 120px;
  background: ${({ theme }) => theme.colors.box};
  border-radius: 5px;
  cursor: pointer;
  padding: 10px;
  color: black;
  z-index: 2;

  .code {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
  }
`;

const LevelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 50px;
`;

const LeftStand = styled.div`
  position: absolute;
  height: calc(100% + 50px);
  top: 0px;
  width: 13px;
  left: -2px;
  background: #dbdbdf;
  border-radius: 3px;
`;

const RightStand = styled.div`
  position: absolute;
  background: #dbdbdf;
  height: calc(100% + 50px);
  top: 0px;
  width: 13px;
  border-radius: 3px;
  right: -2px;
`;

function AddBay(props) {
  return (
    <LocationContainer {...props}>
      <div className="d-flex align-items-center justify-content-center">
        <Plus /> Bay
      </div>
    </LocationContainer>
  );
}

function RackDetailHeader({ rackId, rackOptions, onChangeRack, breadcrumbItems }) {
  const selectOptions = rackOptions?.map(opt => ({
    value: opt.value,
    label: opt.text,
  }));

  return (
    <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
      <WarehouseSettingsBreadcrumb items={breadcrumbItems} />
      <Select
        value={rackId}
        onChange={onChangeRack}
        options={selectOptions}
        style={{ minWidth: 200 }}
      />
    </Flex>
  );
}

const RelativeContainer = styled.div`
  position: relative;
`;

const AddLevelContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80px;
  border: 2px dashed ${({ theme }) => theme.colors.grey};
  color: ${({ theme }) => theme.colors.grey};
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    background: ${({ theme }) => theme.colors.box};
  }
`;

export function RackDetails({
  rackId,
  rackOptions,
  onChangeRack,
  data: zoneData,
  zoneId,
  refreshData: refreshZoneData,
}) {
  const { warehouseData } = useWarehouseDetails();
  const { data: zoneMeta } = ZoneDetailHook(zoneId);
  const { data, locationData, refreshData, loading } = RackDetailHook(rackId);
  const [bayPopup, setBayPopup] = useState(false);
  const [levelsData, setLevelsData] = useState([]);
  const [location, setLocation] = useState(null);

  const breadcrumbItems = useMemo(
    () => [
      {
        label: warehouseData?.name ?? 'Warehouse',
        to: '/app/settings/warehouse',
      },
      {
        label: zoneMeta?.code ? `${zoneMeta.code}: ${zoneMeta.type}` : '…',
        to: `/app/settings/warehouse/zones/${zoneId}`,
      },
      {
        label: data?.code ?? '…',
      },
    ],
    [warehouseData?.name, zoneMeta?.code, zoneMeta?.type, zoneId, data?.code],
  );

  const zoneDetails = useMemo(
    () => zoneData?.find(x => x.zoneId === zoneId),
    [zoneData, zoneId],
  );

  const openLocationPopup = loc => {
    setLocation(loc);
  };

  const closeLocationPopup = () => {
    setLocation(null);
  };

  useEffect(() => {
    if (CommonUtility.isValidArray(locationData)) {
      setLevelsData(locationData.reverse());
    }
  }, [locationData]);

  const createNewBay = (level, bay) => {
    setBayPopup({
      level,
      bay,
    });
  };

  const closePopup = () => {
    setBayPopup(null);
  };

  const addLevel = () => {
    const levels = [...levelsData];
    levels.unshift({
      level: levelsData[0]?.level + 1,
    });

    setLevelsData(levels);
  };

  return (
    <>
      <RackDetailHeader
        rackOptions={rackOptions}
        onChangeRack={onChangeRack}
        rackId={rackId}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="row mt-2">
        <div className="col-4 pl-0">
          <RackDimensionsDetails
            zoneDetails={zoneDetails}
            detailLoading={loading}
            data={data}
            refreshZoneData={refreshZoneData}
          />
        </div>
        <ShadowBox className="col-8">
          <LevelsContainer>
            <RelativeContainer>
              <LocationRenderer>
                <AddLevelContainer onClick={addLevel}>
                  <Plus className="mr-1" /> Level
                </AddLevelContainer>
              </LocationRenderer>
            </RelativeContainer>
            {levelsData?.map(location => (
              <RelativeContainer key={`level-${location.level}`}>
                <LocationRenderer>
                  <div className="name">Level {location.level}</div>
                  {location.locations?.map(location =>
                    location.locations?.length > 1 ? (
                      <MultipleLocationRenderer
                        location={location}
                        openLocationPopup={openLocationPopup}
                      />
                    ) : (
                      <LocationContainer
                        onClick={() => openLocationPopup(location.locations[0])}
                        key={location.locations[0]._id}
                      >
                        {location.locations[0].type === 'pallet' ? (
                          <StackSimple size={20} />
                        ) : (
                          <Tray size={20} />
                        )}

                        {location.locations[0].type === 'pallet' ? (
                          <span className="my-2">Pallet</span>
                        ) : (
                          <span className="my-2">Bin</span>
                        )}
                        <MuteText className="code">
                          {location.locations[0]?.code}
                        </MuteText>
                      </LocationContainer>
                    ),
                  )}
                  <AddBay
                    onClick={() =>
                      createNewBay(
                        location.level,
                        location?.locations?.[location?.locations?.length - 1]
                          ?.locations?.[0]?.coordinates?.bay,
                      )
                    }
                  />
                </LocationRenderer>
              </RelativeContainer>
            ))}
            {CommonUtility.isValidArray(levelsData) && (
              <>
                <LeftStand />
                <RightStand />
              </>
            )}
          </LevelsContainer>
        </ShadowBox>
      </div>
      <AddBayPopup
        refreshData={refreshData}
        open={bayPopup}
        rackId={rackId}
        onClose={closePopup}
      />
      <EditBayPopup
        open={!!location}
        onClose={closeLocationPopup}
        locationData={location}
        refreshData={refreshData}
      />
    </>
  );
}

const MultipleLocatonContainer = styled.div`
  display: flex;
  flex-direction: column;
  transition: all 0.2s;
  padding-bottom: 25px;
  &:hover {
    padding-bottom: 0px;
    .false-container {
      height: 65px;
    }
    .true-container {
      box-shadow: none;
    }
    row-gap: 5px;
  }
`;

const SmallLocationContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 120px;
  background: ${({ theme }) => theme.colors.box};
  border-radius: 5px;
  cursor: pointer;
  padding: 10px;
  color: black;
  box-shadow: 0px -5px 7px rgba(57, 57, 72, 0.07);

  &.no-shadow {
    box-shadow: none;
  }

  transition: all 0.2s;
  .code {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 12px;
  }
`;

const FalseContainer = styled.div`
  transition: all 0.2s;
  height: 35px;
`;

function MultipleLocationRenderer({ location, openLocationPopup }) {
  return (
    <MultipleLocatonContainer>
      {location.locations.map((loc, index) => (
        <FalseContainer
          className="false-container"
          onClick={() => openLocationPopup(loc)}
        >
          <SmallLocationContainer
            className={`true-container ${index === 0 ? 'no-shadow' : ''}`}
          >
            <div className="d-flex align-items-center">
              {loc.type === 'pallet' ? <StackSimple /> : <Tray />}
              <span className="ml-1 code">{loc.code}</span>
            </div>
            {loc.type === 'pallet' ? (
              <div className="d-flex align-items-center">
                <StackSimple color={theme.colors.grey} className="mr-1" />
                <MuteText>Pallet</MuteText>
              </div>
            ) : (
              <div className="d-flex align-items-center">
                <Tray color={theme.colors.grey} className="mr-1" />
                <MuteText>Bin</MuteText>
              </div>
            )}
          </SmallLocationContainer>
        </FalseContainer>
      ))}
    </MultipleLocatonContainer>
  );
}
